/**
 *
 * Picker
 *
 */

import React, { useEffect, useRef, useState } from 'react';
import _classNames from 'classnames';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';
import ButtonBase from '@material-ui/core/ButtonBase';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import { BodyFont } from 'utils/theme';

const useStyles = makeStyles(theme => ({
  container: {
    zIndex: 1301,
    position: 'absolute',
    width: 360,
    border: '1px solid #D8D8D8',
    borderRadius: 4,
    top: 54,
    backgroundColor: '#FFFFFF',
    boxShadow: '0 0 7px rgba(0,0,0,.1)!important',
    '-moz-box-shadow': '0 0 7px rgba(0,0,0,.1)!important',
    '-webkit-box-shadow': '0 0 7px rgba(0,0,0,.1)!important',
  },
  headerWrapper: {
    display: 'flex',
    justifyContent: 'center',
    padding: 8,
    borderBottom: '1px solid #D8D8D8',
  },
  header: {
    fontFamily: BodyFont,
    fontSize: '1.125rem',
    color: theme.palette.text.primary,
    fontWeight: 700,
  },
  contentContainer: {
    display: 'flex',
  },
  content: {
    width: '50%',
    height: ({ modal }) => (modal ? 128 : 188),
    overflowY: 'scroll',
    padding: '4px 16px',
    '&:first-child': {
      borderRight: '0.5px solid #D8D8D8',
    },
    '&:last-child': {
      borderLeft: '0.5px solid #D8D8D8',
    },
  },
  optionsWrapper: {
    position: 'relative',
  },
  option: {
    width: '100%',
    height: 30,
    ...theme.typography.body2,
    color: theme.palette.text.primary,
    '& > span': {
      visibility: 'hidden',
    },
  },
  selectedOption: {
    color: '#FFFFFF',
    backgroundColor: theme.palette.primary.main,
    borderRadius: 15,
  },
  disabledOption: {
    color: 'rgba(0, 0, 0, 0.26)',
  },
  footerWrapper: {
    display: 'flex',
    justifyContent: 'center',
    borderTop: '1px solid #D8D8D8',
    padding: 16,
  },
  submit: {
    width: '100%',
    backgroundColor: theme.palette.primary.main,
    color: '#FFFFFF',
    padding: 8,
    borderRadius: 17,
    height: 34,
    maxWidth: 300,
    display: 'flex',
    alignItems: 'center',
    ...theme.typography.button,
  },
}));

function Picker({
  locale,
  value,
  modal,
  onChange,
  onSubmit,
  preventDisabledDates = false,
  disableFutureDates,
  disablePreviousDates,
}) {
  const classes = useStyles({ modal });
  const selectedMonth = useRef(!!value);
  const selectedYear = useRef(!!value);
  const [internalValue, setInternalValue] = useState(
    value ||
      moment()
        .month(0)
        .year(new Date().getFullYear() - 13),
  );

  useEffect(() => {
    const elements = document.getElementsByClassName(classes.selectedOption);

    elements.forEach(element => {
      element.parentElement.parentElement.scrollTo(
        0,
        element.offsetTop - 30 < 0 ? 0 : element.offsetTop - 30,
      );
    });
  }, []);

  useEffect(() => {
    if (
      Boolean(value) &&
      value.isValid() &&
      value.format('yyyy-MM') !== internalValue.format('yyyy-MM')
    ) {
      setInternalValue(value);
    }
  }, [value]);

  useEffect(() => {
    if (selectedMonth.current && selectedYear.current) onChange(internalValue);
  }, [internalValue]);

  const numberOfYears =
    !disableFutureDates && !disablePreviousDates
      ? 140
      : !disablePreviousDates
      ? 107
      : 20;
  const yearsOffset = disablePreviousDates ? 0 : 119;
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  const years = Array.from(new Array(numberOfYears)).map(
    (_, index) => currentYear - yearsOffset + index,
  );
  const localizedMonths = moment()
    .locale(locale.locale)
    .localeData()
    .months();
  const submitLabel = _get(locale, 'ok');

  const handleMonthClick = month => {
    selectedMonth.current = true;
    setInternalValue(moment({ year: internalValue.year(), month }));
  };

  const handleYearClick = year => {
    selectedYear.current = true;
    setInternalValue(moment({ year, month: internalValue.month() }));
  };

  const handleSubmit = () => {
    onSubmit(internalValue);
  };

  const handleMouseDown = e => {
    e.preventDefault();
  };

  return (
    <Box className={classes.container} onMouseDown={handleMouseDown}>
      <Box className={classes.headerWrapper}>
        <Typography className={classes.header}>
          {internalValue
            .locale(locale.locale)
            .format('MMMM, yyyy')
            .toUpperCase()}
        </Typography>
      </Box>
      <Box className={classes.contentContainer}>
        <Box className={classes.content}>
          <Box className={classes.optionsWrapper}>
            {localizedMonths.map((month, index) => {
              const diffInMonths = moment(
                `${internalValue.year()}-${index}`,
                'yyyy-MM',
              ).diff(
                moment(`${currentYear}-${currentMonth}`, 'yyyy-MM'),
                'months',
              );
              const disabled =
                preventDisabledDates &&
                ((disableFutureDates && diffInMonths > -(13 * 12)) ||
                  (disablePreviousDates && diffInMonths < 0));
              return (
                <ButtonBase
                  disabled={disabled}
                  className={_classNames(classes.option, {
                    [classes.selectedOption]:
                      !disabled && internalValue.month() === index,
                    [classes.disabledOption]: disabled,
                  })}
                  onClick={() => handleMonthClick(index)}
                >
                  {month}
                </ButtonBase>
              );
            })}
          </Box>
        </Box>
        <Box className={classes.content}>
          <Box className={classes.optionsWrapper}>
            {years.map(year => {
              const diffInMonths = moment(
                `${year}-${internalValue.month()}`,
                'yyyy-MM',
              ).diff(
                moment(`${currentYear}-${currentMonth}`, 'yyyy-MM'),
                'months',
              );
              const disabled =
                preventDisabledDates &&
                ((disableFutureDates && diffInMonths > -(13 * 12)) ||
                  (disablePreviousDates && diffInMonths < 0));

              return (
                <ButtonBase
                  disabled={disabled}
                  className={_classNames(classes.option, {
                    [classes.selectedOption]:
                      !disabled && internalValue.year() === year,
                    [classes.disabledOption]: disabled,
                  })}
                  onClick={() => handleYearClick(year)}
                >
                  {year}
                </ButtonBase>
              );
            })}
          </Box>
        </Box>
      </Box>
      <Box className={classes.footerWrapper}>
        <ButtonBase className={classes.submit} onClick={handleSubmit}>
          {submitLabel}
        </ButtonBase>
      </Box>
    </Box>
  );
}

Picker.propTypes = {
  locale: PropTypes.object,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onSubmit: PropTypes.func,
  preventDisabledDates: PropTypes.bool,
  disableFutureDates: PropTypes.bool,
  disablePreviousDates: PropTypes.bool,
};

export default Picker;
